/**
 * Task activity drawer store.
 * Written by `<TaskActivitySubscriber />` and the drawer itself; read by the
 * drawer, its tabs and the floating action button's unread badge.
 *
 * The REST feed is the baseline and the SignalR `ActivityDelta` stream keeps it
 * live. Seen state is applied optimistically so the badge reacts immediately,
 * then reconciled by the next baseline fetch.
 */

import { create } from 'zustand';
import type { TaskActivityGroupView, TaskActivityItemView } from '@actschurch/ccms-sdk';

export type TaskActivityTab = 'comments' | 'notes' | 'online';

/** A live row as it arrives on the wire, before it becomes a feed item. */
export interface TaskActivityDelta {
  activityTypeId: number;
  itemId: number;
  taskId: number;
  taskTitle: string;
  statusId: number;
  priorityId: number;
  dueDate?: string | null;
  createdBy: string;
  createdByName: string;
  createdByAvatar?: string | null;
  body?: string | null;
  bodyPlain?: string | null;
  occurredAt?: string | null;
  parentId?: number | null;
  isActionRequired: boolean;
  actionLabel?: string | null;
  isMention: boolean;
  isSeen: boolean;
  sequence: number;
}

/** Activity kinds, mirroring the seeded TaskActivityType lookup. */
export const ACTIVITY_TYPE_COMMENT = 1;
export const ACTIVITY_TYPE_NOTE = 2;

/** Stable identity for one item across both kinds. */
export const activityKey = (activityTypeId: number, itemId: number) => `${activityTypeId}:${itemId}`;

interface TaskActivityState {
  groups: TaskActivityGroupView[];
  totalUnread: number;
  unreadComments: number;
  unreadNotes: number;

  drawerOpen: boolean;
  activeTab: TaskActivityTab;

  /** Whether already-read items are shown alongside unread ones. */
  showRead: boolean;

  /**
   * Items that became read during this drawer session and so stay visible until
   * it closes.
   *
   * Seen is marked automatically after ~1s of dwell, so without this a row
   * would disappear out from under the user while they are still reading it.
   * Keyed off the marking itself rather than a snapshot taken when the drawer
   * opens — the drawer can open before the baseline has loaded, which would
   * capture an empty snapshot and reintroduce the very problem this prevents.
   */
  stickyKeys: Set<string>;

  /** User IDs currently viewing TaskManagement, from the hub's presence channel. */
  presentUserIds: string[];

  /** Items already sent to the server, so a replayed buffer is not re-sent. */
  markedSeen: Set<string>;

  setFeed: (groups: TaskActivityGroupView[]) => void;
  applyDelta: (delta: TaskActivityDelta) => void;
  markSeenLocal: (keys: string[]) => void;
  setDrawerOpen: (open: boolean) => void;
  setActiveTab: (tab: TaskActivityTab) => void;
  setShowRead: (show: boolean) => void;
  setPresentUsers: (userIds: string[]) => void;
  addPresentUser: (userId: string) => void;
  removePresentUser: (userId: string) => void;
  reset: () => void;
}

const EMPTY = {
  groups: [] as TaskActivityGroupView[],
  totalUnread: 0,
  unreadComments: 0,
  unreadNotes: 0,
  presentUserIds: [] as string[],
};

/** Recompute the rollups from the groups so counts can never drift from the rows. */
function withCounts(groups: TaskActivityGroupView[]) {
  const recounted = groups.map((g) => ({
    ...g,
    unreadComments: g.items.filter((i) => !i.isSeen && i.activityTypeID === ACTIVITY_TYPE_COMMENT).length,
    unreadNotes: g.items.filter((i) => !i.isSeen && i.activityTypeID === ACTIVITY_TYPE_NOTE).length,
    lastActivityDateTime: g.items[0]?.occurredAt ?? g.lastActivityDateTime,
  }));

  const unreadComments = recounted.reduce((sum, g) => sum + g.unreadComments, 0);
  const unreadNotes = recounted.reduce((sum, g) => sum + g.unreadNotes, 0);

  return {
    groups: recounted,
    unreadComments,
    unreadNotes,
    totalUnread: unreadComments + unreadNotes,
  };
}

const sortItems = (items: TaskActivityItemView[]) =>
  [...items].sort((a, b) => (b.occurredAt ?? '').localeCompare(a.occurredAt ?? ''));

const sortGroups = (groups: TaskActivityGroupView[]) =>
  [...groups].sort((a, b) => (b.lastActivityDateTime ?? '').localeCompare(a.lastActivityDateTime ?? ''));

export const useTaskActivityStore = create<TaskActivityState>((set) => ({
  ...EMPTY,
  drawerOpen: false,
  activeTab: 'comments',
  showRead: false,
  stickyKeys: new Set<string>(),
  markedSeen: new Set<string>(),

  // The baseline replaces the rows wholesale — it is authoritative, and unlike
  // the badge stores there is no partial-snapshot case to merge around.
  setFeed: (groups) =>
    set(() => ({ ...withCounts(sortGroups(groups.map((g) => ({ ...g, items: sortItems(g.items) })))) })),

  applyDelta: (delta) =>
    set((state) => {
      const item: TaskActivityItemView = {
        activityTypeID: delta.activityTypeId,
        itemID: delta.itemId,
        taskID: delta.taskId,
        body: delta.body,
        occurredAt: delta.occurredAt,
        createdBy: delta.createdBy,
        createdByName: delta.createdByName,
        createdByAvatar: delta.createdByAvatar,
        isSeen: delta.isSeen,
        isMention: delta.isMention,
        parentID: delta.parentId,
        isActionRequired: delta.isActionRequired,
        actionLabel: delta.actionLabel,
        actionStatus: null,
      };

      const groups = [...state.groups];
      const groupIndex = groups.findIndex((g) => g.taskID === delta.taskId);

      if (groupIndex === -1) {
        groups.push({
          taskID: delta.taskId,
          taskTitle: delta.taskTitle,
          statusID: delta.statusId,
          priorityID: delta.priorityId,
          dueDate: delta.dueDate,
          unreadComments: 0,
          unreadNotes: 0,
          lastActivityDateTime: delta.occurredAt,
          items: [item],
        });
      } else {
        const group = groups[groupIndex];

        // Reconnects replay rows the baseline already carries, so drop anything
        // we have seen before rather than inserting the same row twice.
        const exists = group.items.some(
          (i) => i.activityTypeID === item.activityTypeID && i.itemID === item.itemID
        );
        if (exists) return state;

        groups[groupIndex] = {
          ...group,
          // A stale task title should not overwrite a fresher one, but an empty
          // one should be filled in.
          taskTitle: group.taskTitle || delta.taskTitle,
          items: sortItems([item, ...group.items]),
        };
      }

      // A row that lands while the drawer is open is pinned for this session —
      // notably the user's own item, which arrives already read and would
      // otherwise never appear.
      const stickyKeys = state.drawerOpen
        ? new Set(state.stickyKeys).add(activityKey(item.activityTypeID, item.itemID))
        : state.stickyKeys;

      return { ...withCounts(sortGroups(groups)), stickyKeys };
    }),

  markSeenLocal: (keys) =>
    set((state) => {
      if (keys.length === 0) return state;
      const keySet = new Set(keys);

      // Pin anything read while the drawer is open so it does not vanish
      // mid-scroll. Covers every path that marks: dwell, "mark all as read",
      // and a baseline that arrives after the drawer was already open.
      const stickyKeys = state.drawerOpen
        ? new Set([...state.stickyKeys, ...keys])
        : state.stickyKeys;

      const groups = state.groups.map((g) => ({
        ...g,
        items: g.items.map((i) =>
          !i.isSeen && keySet.has(activityKey(i.activityTypeID, i.itemID)) ? { ...i, isSeen: true } : i
        ),
      }));

      const markedSeen = new Set(state.markedSeen);
      keys.forEach((k) => markedSeen.add(k));

      return { ...withCounts(groups), markedSeen, stickyKeys };
    }),

  // Opening and closing both start a fresh session: whatever was read last time
  // stops being pinned, so the next open shows a genuinely current list.
  setDrawerOpen: (drawerOpen) => set({ drawerOpen, stickyKeys: new Set<string>() }),
  setActiveTab: (activeTab) => set({ activeTab }),
  setShowRead: (showRead) => set({ showRead }),

  setPresentUsers: (presentUserIds) => set({ presentUserIds: Array.from(new Set(presentUserIds)) }),

  addPresentUser: (userId) =>
    set((state) =>
      state.presentUserIds.includes(userId)
        ? state
        : { presentUserIds: [...state.presentUserIds, userId] }
    ),

  removePresentUser: (userId) =>
    set((state) => ({ presentUserIds: state.presentUserIds.filter((id) => id !== userId) })),

  reset: () => set({ ...EMPTY, markedSeen: new Set<string>(), stickyKeys: new Set<string>() }),
}));
